import { NWS_STATE, NWS_USER_AGENT } from '../constants/weather';

export type NwsAlertGeometry =
  | { type: 'Polygon'; coordinates: number[][][] }
  | { type: 'MultiPolygon'; coordinates: number[][][][] };

export type NwsAlertMapFeature = {
  id: string;
  event: string;
  headline: string;
  severity: string;
  geometry: NwsAlertGeometry;
};

type AlertsGeoJson = {
  features?: Array<{
    id?: string;
    geometry?: NwsAlertGeometry | null;
    properties?: {
      id?: string;
      event?: string;
      headline?: string;
      severity?: string;
    };
  }>;
};

async function nwsGeoFetch(url: string, signal?: AbortSignal): Promise<AlertsGeoJson> {
  const init: RequestInit = {
    headers: { Accept: 'application/geo+json', 'User-Agent': NWS_USER_AGENT },
  };
  if (signal) init.signal = signal;
  const res = await fetch(url, init);
  if (!res.ok) throw new Error(`NWS alerts ${res.status}`);
  return res.json() as Promise<AlertsGeoJson>;
}

function parseFeature(
  raw: NonNullable<AlertsGeoJson['features']>[number]
): NwsAlertMapFeature | null {
  const geom = raw.geometry;
  if (!geom || (geom.type !== 'Polygon' && geom.type !== 'MultiPolygon')) return null;
  const props = raw.properties;
  const event = props?.event?.trim();
  if (!event) return null;
  return {
    id: raw.id ?? props?.id ?? `${event}:${props?.headline ?? ''}`,
    event,
    headline: props?.headline?.trim() ?? event,
    severity: props?.severity?.trim() ?? '',
    geometry: geom,
  };
}

/** Active NWS alerts with polygon geometry for a state (filtered to map view later). */
export async function fetchNwsAlertMapFeatures(
  state = NWS_STATE,
  signal?: AbortSignal
): Promise<NwsAlertMapFeature[]> {
  const body = await nwsGeoFetch(
    `https://api.weather.gov/alerts/active?status=actual&area=${encodeURIComponent(state)}`,
    signal
  );
  const byId = new Map<string, NwsAlertMapFeature>();
  for (const f of body.features ?? []) {
    const parsed = parseFeature(f);
    if (parsed) byId.set(parsed.id, parsed);
  }
  return [...byId.values()];
}
